import { BlockItemContainer, BlockListContainer } from "@foodpilot/foods";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { CustomHeader } from "./CustomHeader";
import { PlanningLayout } from "../ActionListLayout";
import { useGetOneActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { Timerange } from "./ActionFields/Timerange";
import { Loading } from "src/components/utils/Loading";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";
import { Property } from "./Property";
import { ActionDescription } from "./ActionFields/ActionDescription";
import { EmptyActionInfo } from "./ActionFields/EmptyActionInfo";
import { EditStatus } from "./ActionFields/EditStatus";

export type EditPlanningPropertiesProps = unknown;
export const EditPlanningProperties = (_props: EditPlanningPropertiesProps) => {
    const { actionId } = useParams();

    const { selectPostsByCampaigns, selectedActionPlan, selectedScore, actionPlanMetadata, getPropertiesByAction } =
        useActionsContext();
    const { data: actionPlanAction, isFetched } = useGetOneActionPlanAction({
        actionPlanActionId: actionId,
        actionPlanID: actionPlanMetadata.actionPlanID,
    });

    if (selectedActionPlan === undefined || actionPlanAction === undefined) {
        return <Loading />;
    }

    const startingToCompare = selectedActionPlan.metadata.startingPost.campaign?.id;
    const targetToAlter = actionPlanAction.endSimulationSsqPost.campaign?.id;
    const postScores = selectPostsByCampaigns(selectedActionPlan, startingToCompare, targetToAlter, true);
    const propertiesByAction = getPropertiesByAction(postScores, actionPlanAction.actionId);

    if (propertiesByAction === undefined) return <Loading />;
    if (isFetched === false) return <Loading />;
    if (actionPlanAction === undefined) return <>{"Bad action choice !"}</>;

    const blocks = propertiesByAction.end.data.map((ssqProperty, index) => {
        const startProperty = propertiesByAction.start.data.find(
            (item) => item.property.id === ssqProperty.property.id,
        );
        const endProperty = propertiesByAction.end.data.find((item) => item.property.id === ssqProperty.property.id);

        const _diff = ScoreService.substractScore(
            startProperty?.score?.value ?? null,
            endProperty?.score?.value ?? null,
        );
        const diff = ScoreService.normalizeScore(_diff, selectedScore?.precision ?? 0).value;
        const title = ssqProperty.property.title ?? "";
        const unit = selectedScore?.unit;

        return (
            <BlockItemContainer
                key={index}
                title={title}
                value={diff}
                unit={unit}
                fields={[<Property startProperty={startProperty} endProperty={endProperty} />]}
            />
        );
    });

    const normalActionLayout = [
        <ActionDescription label={actionPlanAction.action.description} />,
        <EditStatus actionPlanAction={actionPlanAction} />,
        <Timerange key={`timerange-action-block`} actionPlanAction={actionPlanAction} />,
        ...blocks,
    ];

    const actionWithoutPropertiesLayout = [
        <ActionDescription label={actionPlanAction.action.description} />,
        <EditStatus actionPlanAction={actionPlanAction} />,
        <EmptyActionInfo actionPlanAction={actionPlanAction} />,
        <Timerange key={`timerange-action-block`} actionPlanAction={actionPlanAction} />,
    ];

    const blockLayout = blocks.length === 0 ? actionWithoutPropertiesLayout : normalActionLayout;

    return (
        <PlanningLayout>
            <Fragment>
                <BlockListContainer
                    header={
                        <CustomHeader
                            title={`${actionPlanAction.action.title}`}
                            actionId={actionId}
                            actionPlanId={actionPlanMetadata.actionPlanID}
                        />
                    }
                    blocks={blockLayout}
                />
            </Fragment>
        </PlanningLayout>
    );
};
